/**
 * Custom App Bar — hybrid-3c §5.
 *
 * 52px, white, a 1px divider at its foot, absolute over the content column on
 * desktop (tracking the rail as it collapses) and fixed full width on a phone.
 * It carries the breadcrumb and nothing else of the page: the product name
 * lives once, in the rail, and the reader's own settings (language, sign-out)
 * in the rail's profile block. At the right, the two always-visible safety
 * marks: an active impersonation and the non-production environment pill.
 * The section bar hangs under it (SectionTabs).
 */

import React from 'react';
import { AppBar, LoadingIndicator, TitlePortal, useSidebarState, useTranslate } from 'react-admin';
import { alpha, Box, Button, Link, useMediaQuery, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import SwitchAccountRoundedIcon from '@mui/icons-material/SwitchAccountRounded';
import { getImpersonation, stopImpersonation } from '@/utils/impersonation';
import { SectionTabs } from './SectionTabs';
import { APP_BAR_HEIGHT, useSectionTabs } from './useSectionTabs';

// Deployment environment, independent of the Vite build mode (every `vite build`
// runs with MODE=production regardless of the target server).
const APP_ENV = import.meta.env.VITE_APP_ENV ?? (import.meta.env.PROD ? 'production' : 'local');
const IS_PRODUCTION = APP_ENV === 'production';
const IS_LOCAL = import.meta.env.DEV;

interface Crumb {
  label: string;
  to?: string;
}

export const CustomAppBar: React.FC = () => {
  const theme = useTheme();
  const translate = useTranslate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
  const [open] = useSidebarState();
  const { entry, tab, depth, sections } = useSectionTabs();
  const impersonation = getImpersonation();

  // `Destination › Section › Action`: the section only when the reader sees the
  // section bar, and the last crumb only below a section (an edit, create or
  // detail page). Every crumb but the last leads back; below `md` only the last
  // one is shown (hybrid-3c §12).
  const crumbs: Crumb[] = [];
  if (entry) {
    crumbs.push({ label: translate(entry.nameKey, { smart_count: 2, _: entry.name }), to: entry.to });
    if (sections.length > 0 && tab) {
      crumbs.push({ label: translate(tab.labelKey, { smart_count: 2 }), to: tab.to });
    }
    if (depth === 'create') crumbs.push({ label: translate('ra.action.create') });
    else if (depth === 'detail') crumbs.push({ label: translate('shell.breadcrumb.detail') });
    delete crumbs[crumbs.length - 1].to;
  }
  const visibleCrumbs = isNarrow ? crumbs.slice(-1) : crumbs;
  const railWidth = open ? theme.custom.menu.openWidth : theme.custom.menu.closedWidth;

  return (
    <AppBar
      userMenu={false}
      // The default toolbar adds a language menu; the language belongs to the
      // reader and lives in the rail's profile block. The loading indicator stays.
      toolbar={<LoadingIndicator />}
      elevation={0}
      sx={{
        backgroundColor: theme.palette.background.appbar,
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${theme.palette.divider}`,
        height: `${APP_BAR_HEIGHT}px`,
        minHeight: `${APP_BAR_HEIGHT}px`,
        position: isMobile ? 'fixed' : 'absolute',
        top: 0,
        left: isMobile ? 0 : railWidth,
        width: isMobile ? '100%' : `calc(100% - ${railWidth})`,
        zIndex: isMobile ? 1200 : theme.zIndex.appBar,
        transition: 'left 0.2s ease-in-out, width 0.2s ease-in-out',
        '& .MuiToolbar-root': {
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          // Two classes deep, so it beats MUI's own per-breakpoint Toolbar heights.
          minHeight: `${APP_BAR_HEIGHT}px`,
          height: `${APP_BAR_HEIGHT}px`,
          padding: isMobile ? '0 8px' : '0 22px',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, gap: 1.5 }}>
        {visibleCrumbs.length > 0 && (
          <Box
            component="nav"
            aria-label={translate('shell.breadcrumb.label')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 0,
              color: theme.custom.text.tertiary,
              fontSize: '0.8125rem',
            }}
          >
            {visibleCrumbs.map((crumb, index) => (
              <React.Fragment key={`${index}-${crumb.label}`}>
                {index > 0 && <ChevronRightRoundedIcon aria-hidden sx={{ fontSize: '16px', flexShrink: 0 }} />}
                {crumb.to ? (
                  <Link
                    component={RouterLink}
                    to={crumb.to}
                    underline="hover"
                    color="inherit"
                    sx={{ flexShrink: 0, whiteSpace: 'nowrap', borderRadius: theme.custom.radius.small }}
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <Box
                    component="span"
                    aria-current="page"
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: 600,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {crumb.label}
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Box>
        )}
        <TitlePortal sx={{ display: 'none' }} />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, minWidth: 0 }}>
        {impersonation && (
          // Always-visible safety indicator: while it shows, every request runs
          // with the impersonated user's identity and permissions.
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              height: '26px',
              padding: '0 4px 0 10px',
              borderRadius: theme.custom.radius.full,
              backgroundColor: alpha(theme.palette.error.main, 0.12),
              color: theme.palette.error.dark,
              fontSize: '0.75rem',
              fontWeight: 600,
              minWidth: 0,
            }}
          >
            <SwitchAccountRoundedIcon sx={{ fontSize: '16px', flexShrink: 0 }} />
            <Box
              component="span"
              sx={{
                minWidth: 0,
                maxWidth: { xs: '110px', sm: '260px' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {translate('shell.impersonation.active', { name: impersonation.displayName })}
            </Box>
            <Button
              size="small"
              onClick={() => {
                stopImpersonation();
                window.location.hash = '#/';
                window.location.reload();
              }}
              sx={{
                minWidth: 0,
                // Documented exception to the touch minimum: an inline exit inside
                // a 26px indicator, not a standalone action.
                minHeight: '20px',
                height: '20px',
                padding: '0 8px',
                fontSize: '0.75rem',
                fontWeight: 700,
                color: theme.palette.error.dark,
                flexShrink: 0,
              }}
            >
              {translate('shell.impersonation.exit')}
            </Button>
          </Box>
        )}
        {/* The pill marks the environments that are NOT production and nothing
            else: its absence is the signal that this is the real system. */}
        {!IS_PRODUCTION && (
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              gap: 0.75,
              height: '26px',
              padding: '0 10px',
              borderRadius: theme.custom.radius.full,
              backgroundColor: alpha(theme.palette.warning.main, 0.15),
              color: theme.palette.warning.dark,
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '0.5px',
              whiteSpace: 'nowrap',
            }}
          >
            <Box
              component="span"
              sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: theme.palette.warning.dark }}
            />
            {translate(IS_LOCAL ? 'shell.environment.local' : 'shell.environment.nonProduction')}
          </Box>
        )}
      </Box>

      <SectionTabs />
    </AppBar>
  );
};

export default CustomAppBar;
